import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Check, Package, AlertCircle, Sparkles } from 'lucide-react';
import { publicApi, formatApiError } from '../utils/adminApi';
import SEO from '../components/SEO';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'long' });
  } catch {
    return iso;
  }
};

const TrackPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = useState(searchParams.get('id') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lookup = async (idRaw) => {
    const id = (idRaw || '').trim().toUpperCase();
    if (!id) return;
    setError('');
    setOrder(null);
    setLoading(true);
    try {
      const data = await publicApi.track(id);
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err, 'Could not find that tracking ID.'));
    } finally {
      setLoading(false);
    }
  };

  // Auto-search if id present in URL
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) lookup(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchParams({ id: trackingId.trim().toUpperCase() });
    lookup(trackingId);
  };

  return (
    <div className="track-page" data-testid="track-page">
      <SEO
        title="Heritage Delivery Tracking"
        description="Track your Silk & Sentiment commission through its seven gentle stages \u2014 from first stitch to delivery."
        path="/track"
        noIndex
      />
      <section className="track-hero">
        <div className="luxury-container">
          <button
            className="fdp-back-link"
            onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            ← Return Home
          </button>
          <p className="navigation-text" style={{ color: 'var(--accent-gold)', marginTop: 24 }}>Heritage Delivery</p>
          <h1 className="hero-large" style={{ marginTop: 12 }}>Track your commission</h1>
          <p className="body-large" style={{ marginTop: 12, maxWidth: 600 }}>
            Each piece is shepherded by hand through seven gentle stages. Enter your tracking ID below
            to follow its journey.
          </p>

          <form onSubmit={handleSubmit} className="track-search-form">
            <input
              type="text"
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
              placeholder="SS-XXXXXXXX"
              className="track-input"
              data-testid="track-input"
              autoFocus
            />
            <button type="submit" className="btn-primary" data-testid="track-submit" disabled={loading}>
              <Search size={14} /> {loading ? 'Searching…' : 'Track'}
            </button>
          </form>

          {error && (
            <div className="admin-error" style={{ maxWidth: 480 }} data-testid="track-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}
        </div>
      </section>

      {order && (
        <section className="track-results" data-testid="track-result">
          <div className="luxury-container">
            <div className="track-card">
              <div className="track-card-head">
                <div>
                  <p className="navigation-text" style={{ color: 'var(--accent-gold)' }}>{order.tracking_id}</p>
                  <h2 className="heading-1" style={{ marginTop: 6 }}>{order.piece_name}</h2>
                  <p className="body-regular" style={{ marginTop: 6 }}>For {order.customer_name}</p>
                  {(order.fabric || order.embroidery) && (
                    <p className="body-small" style={{ marginTop: 6, color: 'var(--text-secondary)' }}>
                      {[order.fabric, order.embroidery].filter(Boolean).join(' • ')}
                    </p>
                  )}
                </div>
                <div className="track-stage-pill">
                  <Sparkles size={14} />
                  <span>{order.current_stage}</span>
                </div>
              </div>

              <div className="track-timeline">
                {order.stages.map((stage, idx) => {
                  const currentIdx = order.stages.indexOf(order.current_stage);
                  const state = idx < currentIdx ? 'completed' : idx === currentIdx ? 'current' : 'pending';
                  return (
                    <div key={stage} className={`track-step track-step-${state}`}>
                      <div className="track-step-marker">
                        {state === 'completed' ? <Check size={12} /> : state === 'current' ? <Package size={12} /> : <span>{idx + 1}</span>}
                      </div>
                      <div className="track-step-content">
                        <p className="body-regular track-step-label">{stage}</p>
                        {state === 'current' && (
                          <p className="body-small" style={{ color: 'var(--accent-gold)', marginTop: 2 }}>
                            In progress
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="track-meta">
                <div>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Order placed</span>
                  <p className="body-regular">{formatDate(order.created_at)}</p>
                </div>
                <div>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Last update</span>
                  <p className="body-regular">{formatDate(order.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TrackPage;
